import { useWrapAsync } from "@paroicms/front-app-log";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { useEffect, useState } from "react";
import createReference, { updateReference } from "../../../../../api/application/reference";
import type { Reference } from "../../../../../api/infrastructure/reference.formatter";
import { ClientError } from "../../../../../api/shared/http-client";
import { useGetDatabaseCreationInfo } from "../../../../../store/store-hooks";
import { showToast } from "../../../../shared/global-tools/toast-system";
import Spinner from "../../../utils/Spinner/Spinner";
import "./CreateDatabaseStepOne.scss";

export interface CreateDatabaseStepProps {
  databaseIdParam?: number;
  navigateToStep(step: number): void;
  reference?: Reference;
}

export default function CreateDatabaseStepOne({
  navigateToStep,
  reference,
}: CreateDatabaseStepProps) {
  const { getDatabaseCreationInfo, setDatabaseCreationInfo } = useGetDatabaseCreationInfo();
  const databaseCreationInfo = getDatabaseCreationInfo();
  const [name, setName] = useState("");
  const [description, setDescription] = useState<string>();
  const [dataSource, setDataSource] = useState<string>();
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<Map<string, string>>(new Map());
  const wrapAsync = useWrapAsync();

  const onNameChange = (val: string) => {
    const oldErrors = new Map(errors);
    if (val === "") {
      oldErrors.set("name", "Ce champ est requis");
      setErrors(oldErrors);
    } else {
      oldErrors.delete("name");
      setErrors(oldErrors);
    }
    setName(val);
  };
  const onDescriptionChange = (val: string) => {
    setDescription(val);
  };
  const onDataSourceChange = (val: string) => {
    setDataSource(val);
  };
  const goToNextStep = wrapAsync(async () => {
    if (reference !== undefined) {
      setDatabaseCreationInfo({
        referenceId: reference.id,
      });
      navigateToStep(2);
      return;
    }
    if (name === "") return;
    try {
      setLoading(true);
      let createdReference: Reference;
      if (databaseCreationInfo) {
        createdReference = await updateReference(databaseCreationInfo.referenceId, {
          name,
          description,
          data_source: dataSource,
        });
      } else {
        createdReference = await createReference({
          name,
          description,
          data_source: dataSource,
        });
      }
      setDatabaseCreationInfo({
        referenceId: createdReference.id,
      });
      showToast("Enregistré avec succès", { severity: "success" });
      navigateToStep(2);
    } catch (error) {
      let errorMessage = "Erreur lors de l'enregistrement";
      if (error instanceof ClientError) errorMessage = error.message;
      console.error("Error creating/updating reference:", error);
      showToast(errorMessage, { severity: "info" });
    } finally {
      setLoading(false);
    }
  });

  useEffect(() => {
    if (reference) {
      setName(reference.name ?? "");
      setDescription(reference.description);
      setDataSource(reference.dataSource);
    }
  }, [reference]);

  if (loading) return <Spinner />;
  return (
    <div className="CreateDatabaseStepOne">
      <div className="CreateDatabaseStepOne-container">
        <h2 className="CreateDatabaseStepOne-title">Configuration générale du référentiel</h2>

        <div className="CreateDatabaseStepOne-form">
          <label className="Field">
            <span className="Field-label">
              Nom du référentiel <span className="Field-asterisk">*</span>
            </span>
            <InputText
              className="Field-input w-full"
              placeholder="Champ requis *"
              value={name}
              onChange={(e) => onNameChange(e.target.value)}
              invalid={!!errors.get("name")}
              disabled={reference !== undefined}
            />
            <small className="Field-errorMessage">{errors.get("name")}</small>
          </label>

          <label className="Field">
            <span className="Field-label" style={{ fontWeight: 400 }}>
              Source de donnée
            </span>
            <InputText
              className="Field-input w-full"
              placeholder="Entrez la source de donnée"
              value={dataSource ?? ""}
              onChange={(e) => onDataSourceChange(e.target.value)}
              disabled={reference !== undefined}
            />
          </label>

          <label className="Field">
            <span className="Field-label" style={{ fontWeight: 400 }}>
              Description du référentiel
            </span>
            <InputTextarea
              rows={10}
              className="Field-textarea w-full"
              value={description}
              onChange={(e) => onDescriptionChange(e.target.value)}
              disabled={reference !== undefined}
            />
          </label>
        </div>
      </div>

      <Button
        style={{ display: "flex", justifySelf: "flex-end", marginTop: "37px" }}
        className="Btn primary"
        label="Continuer"
        onClick={goToNextStep}
        disabled={errors.size > 0 || name === "" || loading}
      />
    </div>
  );
}
